import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import TableContainer from "../../BaseComponents/BaseTable";
import BaseModal from "../../BaseComponents/BaseModal";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseSelect from "../../BaseComponents/BaseSelect";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseDatePicker from "../../BaseComponents/BaseDatePicker";
import BaseTooltip from "../../BaseComponents/BaseTooltip";
import {
  InterviewLabel,
  AddInterviewLabel,
  INTERVIEW_STATUS_OPTIONS,
  CompanyLabel,
  AliasedLabel,
  TechnologyLabel,
  RoundLabel,
  ExperienceLabel,
  DateLabel,
  StatusLabel,
  ACCESSOR_KEYS,
  ScheduledBy,
  InterviewStatus,
  InterviewDate,
  Center,
  ChangeStatus,
  StaffNameLabel,
  StatusFulfilled,
  EditInterviewLabel,
} from "../../Constant/Interview";
import {
  Action,
  Search,
  Save,
  updateLeadStatus,
  Update,
  Contact,
  Remarks,
  Company,
  Delete,
} from "../../Constant";
import { checkStatusCodeSuccess, errHandler } from "../../Constant/common";
import { DeleteMessage } from "../../Constant/Center";
import { InputPlaceHolder, SelectPlaceHolder, validationMessages } from "../../Constant/validation";
import { listOfCompanyApi } from "../../Api/companyApi";
import { listOfContactApi } from "../../Api/contactApi";
import {
  createInterviewApi,
  listOfInterviewApi,
  listOfDeveloperApi,
  listOfSchedulerwApi,
  updateInterviewStatusApi,
  deleteInterviewApi,
  updateInterviewApi,
  viewInterview,
} from "../../Api/interviewApi";
import { useSelectedCenter } from "../CenterContext";
import { listOfAliasedApi } from "../../Api/AliasedApi";
import { listOfTechnologyApi } from "../../Api/TechnologyApi";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import { truncateText } from "../../Constant/Lead";
import { Plus, Eye, Edit2, Trash2, TrendingUp, Clipboard, AlertCircle } from "react-feather";
import { useTableQueryParams } from "../../utils/tableUtils";

/* ── Status config ── */
const STATUS_CFG = {
  Selected: { color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  Rejected: { color: "#f43f5e", bg: "rgba(244,63,94,0.12)" },
  "On Hold": { color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
};

const TipCell = (val, max = 20, color = "#6366f1") => {
  const t = truncateText(val, max);
  return val && val.length > max ? (
    <BaseTooltip label={val} position="top" maxWidth={260} color={color}>
      <span style={{ cursor: "default", borderBottom: "1px dashed var(--glass-border)" }}>{t}</span>
    </BaseTooltip>
  ) : (
    <span>{val || "—"}</span>
  );
};

const interviewValidationSchema = yup.object({
  center_id: yup.number().required(validationMessages.required(Center)),
  company_id: yup.number().required(validationMessages.required(Company)),
  contact_id: yup.number().required(validationMessages.required(Contact)),
  auth_id: yup.number().required(validationMessages.required(StaffNameLabel)),
  scheduled_by: yup.number().required(validationMessages.required(ScheduledBy)),
  aliased_id: yup.number().required(validationMessages.required(AliasedLabel)),
  technology_id: yup.number().required(validationMessages.required(TechnologyLabel)),
  experience: yup.string().required(validationMessages.required(ExperienceLabel)),
  round: yup.number().required(validationMessages.required(RoundLabel)),
  date: yup.date().required(validationMessages.required(DateLabel)),
});

const Interview = () => {
  document.title = InterviewLabel;
  const { selectedCenterPayload } = useSelectedCenter();
  const { setLoading } = usePageLoader();

  /* ── table params state ── */
  const {
    currentPage,
    customPageSize,
    searchValue,
    sortOrder,
    columnName,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    resetToFirstPage,
  } = useTableQueryParams({ customPageSize: 5, columnName: "id", sortOrder: "DESC" });

  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState("");
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [interviewIdToDelete, setInterviewIdToDelete] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [contactList, setContactList] = useState([]);
  const [developerList, setDeveloperList] = useState([]);
  const [schedulerList, setSchedulerList] = useState([]);
  const [aliasedList, setAliasedList] = useState([]);
  const [technologyList, setTechnologyList] = useState([]);
  const [statusFormData, setStatusFormData] = useState({
    id: null,
    status: "",
    remarks: "",
    scheduled_by: "",
    round: "",
    date: "",
  });

  const interviewFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      center_id: selectedCenterPayload?.center_id || "",
      company_id: "",
      contact_id: "",
      auth_id: "",
      scheduled_by: "",
      aliased_id: "",
      technology_id: "",
      experience: "",
      round: "",
      status: "",
      remarks: "",
      date: new Date().toISOString().split("T")[0],
    },
    validationSchema: interviewValidationSchema,
    onSubmit: async (values) => {
      setModalLoading(true);
      try {
        const payload = {
          ...values,
          company_id: Number(values.company_id),
          contact_id: Number(values.contact_id),
          auth_id: Number(values.auth_id),
          scheduled_by: Number(values.scheduled_by),
          aliased_id: Number(values.aliased_id),
          technology_id: Number(values.technology_id),
          experience: values.experience,
          round: Number(values.round),
          center_id: editId ? undefined : values.center_id,
          id: editId || undefined,
          status: values.status || undefined,
          remarks: values.remarks || undefined,
        };
        const response = editId
          ? await updateInterviewApi(payload)
          : await createInterviewApi(payload);
        if (checkStatusCodeSuccess(response?.statusCode)) {
          toast.success(response?.message);
          setShowAddModal(false);
          setEditId(null);
          if (currentPage === 1) {
            fetchInterviewList();
          } else {
            resetToFirstPage();
          }
        } else {
          toast.error(response?.message || "Failed to save interview");
        }
      } catch (err) {
        errHandler(err);
      } finally {
        setModalLoading(false);
      }
    },
  });

  const fetchDropdowns = React.useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        listOfCompanyApi({}),
        listOfContactApi({}),
        listOfDeveloperApi({ center_id: selectedCenterPayload.center_id }),
        listOfSchedulerwApi({ center_id: selectedCenterPayload.center_id }),
        listOfAliasedApi({}),
        listOfTechnologyApi({}),
      ]);
      const [companies, contacts, developers, schedulers, aliased, technology] = results;
      if (companies.status === StatusFulfilled)
        setCompanyList(
          companies?.value?.data?.map((c) => ({ value: c.id, label: c.company_name })) || [],
        );
      if (contacts.status === StatusFulfilled)
        setContactList(
          contacts?.value?.data?.map((c) => ({ value: c.id, label: c.contact_name })) || [],
        );
      if (developers.status === StatusFulfilled)
        setDeveloperList(
          developers?.value?.data?.map((d) => ({ value: d.id, label: d.name })) || [],
        );
      if (schedulers.status === StatusFulfilled)
        setSchedulerList(
          schedulers?.value?.data?.map((s) => ({ value: s.id, label: s.name })) || [],
        );
      if (aliased.status === StatusFulfilled)
        setAliasedList(
          aliased?.value?.data?.listOfAliased?.map((s) => ({
            value: s.id,
            label: s.aliased_name,
          })) || [],
        );
      if (technology.status === StatusFulfilled)
        setTechnologyList(
          technology?.value?.data?.listOfTechnology?.map((s) => ({
            value: s.id,
            label: s.technology_name,
          })) || [],
        );
    } catch (e) {
      return e;
    }
  }, [selectedCenterPayload]);

  const fetchInterviewList = React.useCallback(() => {
    setLoading(true);
    listOfInterviewApi({
      ...selectedCenterPayload,
      order: [columnName, sortOrder],
      pageNumber: currentPage,
      pageSize: customPageSize,
      search: searchValue || undefined,
    })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setTableData(resp?.data?.listOfInterview || []);
          setTotalRecords(resp?.data?.totalRecordsCount || 0);
          setTotalPages(resp?.data?.totalPages || 1);
          setTotalNumberOfRows(resp?.data?.numberOfRows || 0);
        } else {
          setTableData([]);
          setTotalRecords(0);
          setTotalPages(1);
          setTotalNumberOfRows(0);
        }
      })
      .catch(() => setTableData([]))
      .finally(() => setLoading(false));
  }, [
    selectedCenterPayload,
    columnName,
    sortOrder,
    currentPage,
    customPageSize,
    searchValue,
    setLoading,
  ]);

  useEffect(() => {
    fetchInterviewList();
  }, [fetchInterviewList]);

  const handleStatusChangeClick = React.useCallback(
    async (interview) => {
      if (!schedulerList.length) await fetchDropdowns();
      setStatusFormData({
        id: interview.id,
        status: interview.status || "",
        remarks: interview.remarks || "",
        scheduled_by: interview.scheduled_by || interview?.scheduledBy?.id || "",
        round: interview.round || "",
        date: interview.date || "",
      });
      setShowStatusModal(true);
    },
    [schedulerList, fetchDropdowns],
  );

  const handleEditInterview = React.useCallback(
    async (id) => {
      setEditId(id);
      setModalLoading(true);
      try {
        const response = await viewInterview(id);
        if (checkStatusCodeSuccess(response?.statusCode)) {
          const data = response.message;
          interviewFormik.setValues({
            center_id: data.center_id || selectedCenterPayload?.center_id || "",
            company_id: data.company?.id || "",
            contact_id: data.contact?.id || "",
            auth_id: data.authUser?.id || "",
            scheduled_by: data.scheduledBy?.id || "",
            aliased_id: data.aliased?.id || "",
            technology_id: data.technology?.id || "",
            experience: data.experience || "",
            round: data.round || "",
            status: data.status || "",
            remarks: data.remarks || "",
            date: data.date || new Date().toISOString().split("T")[0],
          });
          if (!companyList.length) await fetchDropdowns();
          setShowAddModal(true);
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message);
      } finally {
        setModalLoading(false);
      }
    },
    [companyList.length, fetchDropdowns, interviewFormik, selectedCenterPayload?.center_id],
  );

  const handleStatusSubmit = async () => {
    setModalLoading(true);
    try {
      const response = await updateInterviewStatusApi({
        id: statusFormData.id,
        status: statusFormData.status,
        remarks: statusFormData.remarks,
        scheduled_by: Number(statusFormData.scheduled_by),
        round: Number(statusFormData.round),
        date: statusFormData.date,
      });
      if (checkStatusCodeSuccess(response?.statusCode)) {
        toast.success(response?.message);
        setShowStatusModal(false);
        if (currentPage === 1) {
          fetchInterviewList();
        } else {
          resetToFirstPage();
        }
      } else {
        toast.error(response?.message || "Failed to update interview status");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setModalLoading(false);
    }
  };

  const toggleDelete = React.useCallback((id) => {
    setInterviewIdToDelete(id);
    setDeleteModal((p) => !p);
  }, []);

  const handleDeleteInterview = async () => {
    setDeleteLoader(true);
    try {
      const response = await deleteInterviewApi(interviewIdToDelete);
      if (checkStatusCodeSuccess(response?.statusCode)) {
        toast.success(response?.message || "Deleted successfully");
        if (currentPage === 1) {
          fetchInterviewList();
        } else {
          resetToFirstPage();
        }
      } else toast.error(response?.message || "Failed to delete");
    } catch (err) {
      errHandler(err);
    } finally {
      setDeleteLoader(false);
      toggleDelete(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: ACCESSOR_KEYS.SRNOLABEL,
        cell: (c) => (currentPage - 1) * customPageSize + c.row.index + 1,
        enableColumnFilter: false,
      },
      {
        header: CompanyLabel,
        cell: ({ row }) => TipCell(row.original?.company?.company_name, 18, "#6366f1"),
        enableColumnFilter: false,
      },
      {
        header: AliasedLabel,
        cell: ({ row }) => TipCell(row.original?.aliased?.aliased_name, 16, "#8b5cf6"),
        enableColumnFilter: false,
      },
      {
        header: StaffNameLabel,
        cell: ({ row }) => TipCell(row.original?.authUser?.name, 16, "#06b6d4"),
        enableColumnFilter: false,
      },
      {
        header: TechnologyLabel,
        cell: ({ row }) => TipCell(row.original?.technology?.technology_name, 14, "#10b981"),
        enableColumnFilter: false,
      },
      { header: RoundLabel, accessorKey: ACCESSOR_KEYS.ROUND, enableColumnFilter: false },
      { header: ExperienceLabel, accessorKey: ACCESSOR_KEYS.EXPERIENCE, enableColumnFilter: false },
      {
        header: DateLabel,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const d = row.original?.date;
          if (!d) return "—";
          const [y, m, day] = d.split("-");
          return `${day}-${m}-${y}`;
        },
      },
      {
        header: ScheduledBy,
        cell: ({ row }) => TipCell(row.original?.scheduledBy?.name, 16, "#f59e0b"),
        enableColumnFilter: false,
      },
      {
        header: StatusLabel,
        accessorKey: ACCESSOR_KEYS.STATUS,
        enableColumnFilter: false,
        cell: (c) => {
          const s = c.row.original?.status;
          const cfg = STATUS_CFG[s] || { color: "#64748b", bg: "rgba(100,116,139,0.12)" };
          return s ? (
            <span
              style={{
                padding: "3px 10px",
                borderRadius: 999,
                fontSize: "0.6875rem",
                fontWeight: 700,
                background: cfg.bg,
                color: cfg.color,
                whiteSpace: "nowrap",
              }}
            >
              {s}
            </span>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>—</span>
          );
        },
      },
      {
        header: Action,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
            <BaseTooltip label={Remarks} color="#047857">
              <button
                onClick={() => {
                  setSelectedRemarks(row.original?.remarks || "—");
                  setShowRemarksModal(true);
                }}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: "rgba(16,185,129,0.08)",
                  border: "1px solid rgba(16,185,129,0.2)",
                  color: "#10b981",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Eye size={12} strokeWidth={2} />
              </button>
            </BaseTooltip>
            <BaseTooltip label={ChangeStatus} color="#4f46e5">
              <button
                onClick={() => handleStatusChangeClick(row.original)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: "rgba(99,102,241,0.08)",
                  border: "1px solid rgba(99,102,241,0.2)",
                  color: "var(--brand-indigo)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <TrendingUp size={12} strokeWidth={2} />
              </button>
            </BaseTooltip>
            <BaseTooltip label={EditInterviewLabel} color="#b45309">
              <button
                onClick={() => handleEditInterview(row.original.id)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: "rgba(245,158,11,0.08)",
                  border: "1px solid rgba(245,158,11,0.2)",
                  color: "#f59e0b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Edit2 size={12} strokeWidth={2} />
              </button>
            </BaseTooltip>
            <BaseTooltip label={Delete} color="#be123c">
              <button
                onClick={() => toggleDelete(row.original.id)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: "rgba(244,63,94,0.08)",
                  border: "1px solid rgba(244,63,94,0.2)",
                  color: "#f43f5e",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Trash2 size={12} strokeWidth={2} />
              </button>
            </BaseTooltip>
          </div>
        ),
      },
    ],
    [currentPage, customPageSize, handleStatusChangeClick, handleEditInterview, toggleDelete],
  );

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* ── Page header ── */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(99,102,241,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Clipboard size={20} strokeWidth={1.75} style={{ color: "var(--brand-indigo)" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {InterviewLabel}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {totalRecords > 0 ? `${totalRecords} interviews` : "Manage your interviews"}
            </p>
          </div>
        </div>
        <BaseButton
          color="primary"
          onClick={() => {
            fetchDropdowns();
            interviewFormik.resetForm();
            setEditId(null);
            setShowAddModal(true);
          }}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={15} strokeWidth={2.5} /> {AddInterviewLabel}
        </BaseButton>
      </div>

      {/* ── Table ── */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {tableData?.length > 0 || searchValue ? (
          <TableContainer
            columns={columns}
            data={tableData || []}
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
            customPageSize={customPageSize}
            setCustomPageSize={handlePageSizeChange}
            fetchData={handlePageChange}
            onSearch={handleSearchChange}
            fetchSortingData={handleSortChange}
            SearchPlaceholder={Search}
            isGlobalFilter
            manualPagination
            manualFiltering
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>No interviews found</p>
          </div>
        )}
      </div>

      {/* ── Add / Edit modal ── */}
      <BaseModal
        isOpen={showAddModal}
        toggler={() => {
          if (!modalLoading) {
            setShowAddModal(false);
            setEditId(null);
            interviewFormik.resetForm();
          }
        }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(99,102,241,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {editId ? (
                <Edit2 size={14} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
              ) : (
                <Plus size={14} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />
              )}
            </div>
            <span>{editId ? EditInterviewLabel : AddInterviewLabel}</span>
          </div>
        }
        submit={interviewFormik.handleSubmit}
        submitText={editId ? Update : Save}
        loader={modalLoading}
        disabled={modalLoading}
        size="lg"
      >
        <form onSubmit={interviewFormik.handleSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: "0 16px",
            }}
          >
            <BaseSelect
              label={CompanyLabel}
              name="company_id"
              options={companyList}
              value={interviewFormik.values.company_id}
              handleChange={interviewFormik.setFieldValue}
              placeholder={SelectPlaceHolder(CompanyLabel)}
              required
              touched={interviewFormik.touched.company_id}
              handleBlur={interviewFormik.handleBlur}
              error={interviewFormik.touched.company_id && interviewFormik.errors.company_id}
            />
            <BaseSelect
              label={Contact}
              name="contact_id"
              options={contactList}
              value={interviewFormik.values.contact_id}
              handleChange={interviewFormik.setFieldValue}
              placeholder={SelectPlaceHolder(Contact)}
              required
              touched={interviewFormik.touched.contact_id}
              handleBlur={interviewFormik.handleBlur}
              error={interviewFormik.touched.contact_id && interviewFormik.errors.contact_id}
            />
            <BaseSelect
              label={StaffNameLabel}
              name="auth_id"
              options={developerList}
              value={interviewFormik.values.auth_id}
              placeholder={SelectPlaceHolder(StaffNameLabel)}
              handleChange={interviewFormik.setFieldValue}
              required
              touched={interviewFormik.touched.auth_id}
              handleBlur={interviewFormik.handleBlur}
              error={interviewFormik.touched.auth_id && interviewFormik.errors.auth_id}
            />
            <BaseSelect
              label={ScheduledBy}
              name="scheduled_by"
              options={schedulerList}
              value={interviewFormik.values.scheduled_by}
              placeholder={SelectPlaceHolder(ScheduledBy)}
              handleChange={interviewFormik.setFieldValue}
              required
              touched={interviewFormik.touched.scheduled_by}
              handleBlur={interviewFormik.handleBlur}
              error={interviewFormik.touched.scheduled_by && interviewFormik.errors.scheduled_by}
            />
            <BaseSelect
              label={AliasedLabel}
              name="aliased_id"
              options={aliasedList}
              value={interviewFormik.values.aliased_id}
              handleChange={interviewFormik.setFieldValue}
              placeholder={SelectPlaceHolder(AliasedLabel)}
              required
              touched={interviewFormik.touched.aliased_id}
              handleBlur={interviewFormik.handleBlur}
              error={interviewFormik.touched.aliased_id && interviewFormik.errors.aliased_id}
            />
            <BaseSelect
              label={TechnologyLabel}
              name="technology_id"
              options={technologyList}
              value={interviewFormik.values.technology_id}
              placeholder={SelectPlaceHolder(TechnologyLabel)}
              handleChange={interviewFormik.setFieldValue}
              required
              touched={interviewFormik.touched.technology_id}
              handleBlur={interviewFormik.handleBlur}
              error={interviewFormik.touched.technology_id && interviewFormik.errors.technology_id}
            />
            <BaseInput
              label={ExperienceLabel}
              name="experience"
              type="number"
              value={interviewFormik.values.experience}
              placeholder={InputPlaceHolder(ExperienceLabel)}
              handleChange={interviewFormik.handleChange}
              required
              touched={interviewFormik.touched.experience}
              handleBlur={interviewFormik.handleBlur}
              error={interviewFormik.touched.experience && interviewFormik.errors.experience}
            />
            <BaseInput
              label={RoundLabel}
              name="round"
              type="number"
              value={interviewFormik.values.round}
              handleChange={interviewFormik.handleChange}
              placeholder={InputPlaceHolder(RoundLabel)}
              required
              touched={interviewFormik.touched.round}
              handleBlur={interviewFormik.handleBlur}
              error={interviewFormik.touched.round && interviewFormik.errors.round}
            />
            <BaseDatePicker
              label={DateLabel}
              name="date"
              value={interviewFormik.values.date}
              onChange={(v) => interviewFormik.setFieldValue("date", v)}
              onBlur={interviewFormik.handleBlur}
              touched={interviewFormik.touched.date}
              error={interviewFormik.touched.date && interviewFormik.errors.date}
              required
            />
            {editId && (
              <>
                <BaseSelect
                  label={InterviewStatus}
                  name="status"
                  value={interviewFormik.values.status}
                  options={INTERVIEW_STATUS_OPTIONS}
                  handleChange={interviewFormik.setFieldValue}
                />
                <div style={{ gridColumn: "1 / -1" }}>
                  <BaseInput
                    label={Remarks}
                    type="textarea"
                    name="remarks"
                    value={interviewFormik.values.remarks}
                    handleChange={interviewFormik.handleChange}
                  />
                </div>
              </>
            )}
          </div>
        </form>
      </BaseModal>

      {/* ── Status modal ── */}
      <BaseModal
        isOpen={showStatusModal}
        toggler={() => setShowStatusModal(false)}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(99,102,241,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TrendingUp size={14} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
            </div>
            <span>{updateLeadStatus}</span>
          </div>
        }
        size="md"
        submit={handleStatusSubmit}
        loader={modalLoading}
        submitText={Update}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          <BaseSelect
            label={InterviewStatus}
            name="status"
            value={statusFormData.status}
            options={INTERVIEW_STATUS_OPTIONS}
            handleChange={(name, value) => setStatusFormData((p) => ({ ...p, [name]: value }))}
            required
          />
          <BaseSelect
            label={ScheduledBy}
            name="scheduled_by"
            options={schedulerList}
            value={statusFormData.scheduled_by}
            handleChange={(name, value) =>
              setStatusFormData((p) => ({ ...p, scheduled_by: value }))
            }
            placeholder={SelectPlaceHolder(ScheduledBy)}
            required
          />
          <BaseInput
            label={RoundLabel}
            type="number"
            name="round"
            value={statusFormData.round}
            handleChange={(e) => setStatusFormData((p) => ({ ...p, round: e.target.value }))}
            required
          />
          <BaseDatePicker
            label={InterviewDate}
            name="date"
            value={statusFormData.date}
            onChange={(v) => setStatusFormData((p) => ({ ...p, date: v }))}
            required
          />
          <div style={{ gridColumn: "1 / -1" }}>
            <BaseInput
              label={Remarks}
              type="textarea"
              name="remarks"
              value={statusFormData.remarks}
              handleChange={(e) => setStatusFormData((p) => ({ ...p, remarks: e.target.value }))}
            />
          </div>
        </div>
      </BaseModal>

      {/* ── Remarks modal ── */}
      <BaseModal
        isOpen={showRemarksModal}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(16,185,129,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Eye size={14} strokeWidth={2} style={{ color: "#10b981" }} />
            </div>
            <span>{Remarks}</span>
          </div>
        }
        toggler={() => setShowRemarksModal(false)}
        hideSubmit
        size="md"
      >
        <div
          style={{
            whiteSpace: "pre-wrap",
            fontSize: "0.9375rem",
            color: "var(--text-primary)",
            lineHeight: 1.7,
            padding: "4px 0",
            minHeight: 60,
          }}
        >
          {selectedRemarks || <span style={{ color: "var(--text-muted)" }}>No remarks added.</span>}
        </div>
      </BaseModal>

      {/* ── Delete modal ── */}
      <BaseModal
        isOpen={deleteModal}
        title={`${Delete} Interview`}
        toggler={() => toggleDelete(null)}
        submit={handleDeleteInterview}
        submitText={Delete}
        disabled={deleteLoader}
        loader={deleteLoader}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "4px 0" }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(244,63,94,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Trash2 size={16} strokeWidth={2} style={{ color: "#f43f5e" }} />
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "0.9375rem",
              color: "var(--text-primary)",
              lineHeight: 1.5,
            }}
          >
            {DeleteMessage}
          </p>
        </div>
      </BaseModal>
    </div>
  );
};

export default Interview;
